import { formatDateTimeLocal } from '../../utils/dateUtils';
import type { AgencyDataFillRow } from './agencyDataFillRows';

/**
 * The bits of presentation the rail and the pane both need.
 *
 * Here rather than in one of them because both import it: the rail draws a
 * row, the pane draws the same agency's detail. Keeping them in either meant
 * one importing the other that imports it back.
 */

/**
 * Whether a click inside a row landed on something that handles its own.
 *
 * A row is selectable, so it answers clicks anywhere in it. A control inside
 * one -- a button, a link, an input -- has its own job, and selecting the row
 * as well would run two actions for one press.
 *
 * The row itself is excluded, because the row *is* a control: it carries
 * ``role="button"``, so ``closest`` matches it for every click inside it and
 * without this nothing would ever be selectable.
 */
export function clickedAControl(target: EventTarget | null, row: Element) {
  if (!(target instanceof Element)) return false;
  const control = target.closest(
    'button, a, input, select, textarea, [role="button"], [role="switch"], [role="checkbox"]'
  );
  return control !== null && control !== row && row.contains(control);
}

/**
 * A stored timestamp in the reader's own zone and named, or a dash for none.
 *
 * Every stamp on this screen answers "how long ago did somebody do this", which
 * a reader checks against their own clock rather than against Pacific.
 */
export function formatMoment(value: string | null | undefined): string {
  return value ? formatDateTimeLocal(value) : '—';
}

/**
 * Whether this agency has Kinnser turned off.
 *
 * Such an agency is still listed. Leaving it out made "nothing to pull here"
 * look exactly like "no such agency" to somebody searching for one, so the row
 * exists and says why instead.
 */
export function isKinnserOff(row: AgencyDataFillRow): boolean {
  return !row.kinnserEnabled;
}

export const KINNSER_OFF_HINT =
  'Kinnser is off for this agency, so there is nothing to read and nothing to fill.';
